/**
 * CANOA — Slideshow media prefetch-ahead (two-stage).
 *
 * Slide images are loading="lazy" (good — far-below sections only download when
 * approached, which is the bandwidth saving we want). But the native lazy lookahead
 * is small, mobile browsers shrink it further on cellular, and slides 2..N sit
 * horizontally off-screen inside the carousel — where lazy loaders are at their
 * stingiest — so images "popped in" late both on scroll-down AND on swipe.
 *
 * Two IntersectionObserver stages per slideshow (D72):
 *  · STAGE 1 (~1.5 screens below): prime the FIRST slide — the one visible on
 *    arrival. Images → eager / fetchpriority auto; the slide's video gets its
 *    src (see below) and preload="auto" so the buffer builds before arrival.
 *  · STAGE 2 (~0.75 screens below): the user is about to reach the slideshow —
 *    prime the REMAINING slides (2..N) so a swipe never lands on a blank slide.
 *    Images → eager / auto; videos get src but stay preload="metadata" (their
 *    eager poster image covers the slide while the stream spins up on swipe).
 *
 * Video src is NOT set in the markup at all (D63) — and since D72 it is no longer
 * assigned globally at boot either: a video only gets its src when its slideshow
 * is primed. 12 mostly-single-rendition HD-1080p MP4s buffering from page load
 * saturated cellular bandwidth for ~50s and starved every lazy image below (the
 * "empty sections" Laura saw); deferring them keeps the load image-first.
 *
 * Only the media actually displayed on the current device is primed (the other
 * breakpoint's copy is display:none and left alone, so mobile never pulls the
 * desktop file and vice-versa). Pure progressive enhancement: no IO / no JS → the
 * native lazy behavior simply stands (videos stay on their posters).
 */
(() => {
  if (window.__canoaSlidePrefetch || !('IntersectionObserver' in window)) return;
  window.__canoaSlidePrefetch = true;

  // How far below the fold each stage starts priming. ~1.5 viewports gives a
  // 100–160KB WebP enough time to arrive on a slow mobile connection before
  // it's scrolled into view; ~0.75 buys the same lead for the first swipe.
  const LOOKAHEAD_FIRST = '0px 0px 150% 0px';
  const LOOKAHEAD_REST = '0px 0px 75% 0px';

  // D63 — runtime rendition pick, scoped per element since D72. Slide videos
  // render WITHOUT src (one element per slide — no doubled breakpoint copies);
  // the right rendition is copied from data-vid-high/low into src when the
  // slideshow is primed. 749px matches the theme's breakpoint. Chosen once —
  // a desktop window resized across the breakpoint keeps its rendition
  // (acceptable; phones never cross it). The theme editor sets src in Liquid
  // instead (this module isn't relied on there).
  const MOBILE_MQ = window.matchMedia('(max-width: 749px)');
  const assignSource = (v) => {
    if (!v.dataset.vidHigh || v.hasAttribute('src')) return;
    v.src = MOBILE_MQ.matches ? (v.dataset.vidLow || v.dataset.vidHigh) : v.dataset.vidHigh;
  };

  const primeSlide = (slide, { videoPreload }) => {
    slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      // Skip the other-breakpoint copy (mobile:hidden / desktop:hidden) so we never
      // force-load the image that isn't shown on this device.
      if (getComputedStyle(img).display === 'none') return;
      img.loading = 'eager'; // upgrade → the browser begins the fetch now
      if (img.getAttribute('fetchpriority') === 'low') img.setAttribute('fetchpriority', 'auto');
    });
    slide.querySelectorAll('video.slide__video').forEach((video) => {
      if (getComputedStyle(video).display === 'none') return;
      assignSource(video);
      if (videoPreload && video.preload !== videoPreload) video.preload = videoPreload;
    });
  };

  const primeFirst = (slideshow) => {
    const firstSlide = slideshow.querySelector('slideshow-slide');
    if (firstSlide) primeSlide(firstSlide, { videoPreload: 'auto' });
  };

  const primeRest = (slideshow) => {
    slideshow.querySelectorAll('slideshow-slide').forEach((slide, i) => {
      if (i === 0) return; // stage 1's job
      primeSlide(slide, { videoPreload: null }); // metadata stays — poster covers the swipe
    });
  };

  const makeObserver = (onEnter, rootMargin) =>
    new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onEnter(entry.target);
            obs.unobserve(entry.target); // one-shot per slideshow
          }
        }
      },
      { rootMargin }
    );

  const ioFirst = makeObserver(primeFirst, LOOKAHEAD_FIRST);
  const ioRest = makeObserver(primeRest, LOOKAHEAD_REST);

  // D69 — iOS page snap-back gate. WebKit can snap the PAGE's vertical scroll
  // back to align an idle horizontal scroll-snap carousel (constant-velocity
  // drift after release + wobble during drag — caught on Laura's recording at
  // three different carousels). The CSS side (slideshow-styles.liquid) turns
  // snap OFF on phones unless [data-snap-active] is present; here the
  // attribute is set the instant a touch lands on the scroller (before the
  // pan starts, so swiping still snaps natively) and removed ~0.5s after the
  // LAST scroll event — momentum and the settle keep refreshing the timer, so
  // the gesture always completes on a snapped position. All listeners passive.
  const armSnapGates = () => {
    document.querySelectorAll('slideshow-slides').forEach((scroller) => {
      let settleTimer;
      const activate = () => {
        clearTimeout(settleTimer);
        scroller.setAttribute('data-snap-active', '');
      };
      const scheduleRelease = () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => scroller.removeAttribute('data-snap-active'), 500);
      };
      scroller.addEventListener('touchstart', activate, { passive: true });
      scroller.addEventListener('touchend', scheduleRelease, { passive: true });
      scroller.addEventListener('touchcancel', scheduleRelease, { passive: true });
      scroller.addEventListener(
        'scroll',
        () => {
          if (scroller.hasAttribute('data-snap-active')) scheduleRelease();
        },
        { passive: true }
      );
    });
  };

  const boot = () => {
    armSnapGates();
    document.querySelectorAll('slideshow-component').forEach((sc) => {
      ioFirst.observe(sc);
      ioRest.observe(sc);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
